import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BrainCircuit, BarChart2, FlaskConical, BookOpen, Zap,
  TestTube2, Target, History, TrendingUp, Layers, Map,
  BookMarked, Globe, ScanLine, ShieldCheck, Wallet, PieChart, Calculator,
  Compass, Activity, Users, Lock, Lightbulb, ClipboardList, BookMarked as BookMark,
  LineChart, BarChart, Newspaper, GraduationCap, NotebookPen, MessageSquare,
  TrendingDown, Cpu, Radio, Clock, DollarSign, AlignLeft, SlidersHorizontal,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { usePermissions } from '../hooks/usePermissions';
import UpgradeModal from '../components/UpgradeModal';

const CATEGORIES = [
  {
    id: 'ai',
    label: 'AI & Coach',
    color: '#6366F1',
    features: [
      { label: 'AI Coach',     icon: BrainCircuit, path: '/ai-coach',    color: '#6366F1', premium: true },
      { label: 'ACT Practice', icon: Zap,          path: '/act',         color: '#f59e0b' },
      { label: 'Discipline',   icon: Target,       path: '/discipline',  color: '#ef4444' },
      { label: 'Risk Profile', icon: ShieldCheck,  path: '/risk-profile', color: '#10b981' },
    ],
  },
  {
    id: 'markets',
    label: 'Markets',
    color: '#06b6d4',
    features: [
      { label: 'Markets',     icon: BarChart2, path: '/markets',     color: '#06b6d4' },
      { label: 'Derivatives', icon: Layers,    path: '/derivatives', color: '#8b5cf6', premium: true },
      { label: 'Global Cues', icon: Globe,     path: '/global-cues', color: '#10b981' },
    ],
  },
  {
    id: 'how-to',
    label: 'How To',
    color: '#a78bfa',
    features: [
      { label: 'App Guide',        icon: Lightbulb,     path: '/appguide',                             color: '#a78bfa' },
      { label: 'Market Order',     icon: TrendingUp,    path: '/appguide?node=market-order',           color: '#16C784' },
      { label: 'Limit Order',      icon: AlignLeft,     path: '/appguide?node=limit-order',            color: '#06b6d4' },
      { label: 'Stop Loss',        icon: ShieldCheck,   path: '/appguide?node=stop-loss',              color: '#ef4444' },
      { label: 'Trade Planning',   icon: ClipboardList, path: '/appguide?node=trade-planning',         color: '#f59e0b' },
      { label: 'Orders',           icon: NotebookPen,   path: '/appguide?node=orders',                 color: '#8b5cf6' },
      { label: 'Markets',          icon: BarChart2,     path: '/appguide?node=markets',                color: '#06b6d4' },
      { label: 'ACT Practice',     icon: Zap,           path: '/appguide?node=act-practice',           color: '#f59e0b' },
      { label: 'Learning Path',    icon: GraduationCap, path: '/appguide?node=learning-path',          color: '#10b981' },
      { label: 'Trade Journal',    icon: BookOpen,      path: '/appguide?node=trade-journal',          color: '#06b6d4' },
      { label: 'Strategy Feed',    icon: MessageSquare, path: '/appguide?node=strategy-feed',          color: '#f59e0b' },
      { label: 'Performance',      icon: PieChart,      path: '/appguide?node=trade-performance',      color: '#8b5cf6' },
      { label: 'Buy CE (Call)',     icon: TrendingUp,    path: '/appguide?node=buy-call',               color: '#16C784' },
      { label: 'Buy PE (Put)',      icon: TrendingDown,  path: '/appguide?node=buy-put',                color: '#ef4444' },
      { label: 'Buy Futures',      icon: Layers,        path: '/appguide?node=buy-futures',            color: '#a78bfa' },
      { label: 'Capital & Tier',   icon: Wallet,        path: '/appguide?node=capital-tier',           color: '#f59e0b' },
      { label: 'Scanners',         icon: ScanLine,      path: '/appguide?node=scanner',                color: '#06b6d4' },
      { label: 'Analytics',        icon: LineChart,     path: '/appguide?node=analytics',              color: '#10b981' },
      { label: 'Mkt Regime',       icon: Activity,      path: '/appguide?node=market-analysis',        color: '#0ea5e9' },
      { label: 'Backtest',         icon: TestTube2,     path: '/appguide?node=backtest',               color: '#6366f1' },
      { label: 'Algo Flows',       icon: FlaskConical,  path: '/appguide?node=algo-flows',             color: '#f59e0b' },
      { label: 'Global Cues',      icon: Globe,         path: '/appguide?node=global-cues',            color: '#10b981' },
      { label: 'Discipline',       icon: Target,        path: '/appguide?node=discipline-score',       color: '#ef4444' },
      { label: 'AI Coach',         icon: BrainCircuit,  path: '/appguide?node=ai-coach',               color: '#6366f1' },
      { label: 'Risk Profile',     icon: ShieldCheck,   path: '/appguide?node=risk-profile',           color: '#10b981' },
      { label: 'Risk Calc',        icon: Calculator,    path: '/appguide?node=risk-calculator',        color: '#f97316' },
    ],
  },
  {
    id: 'analysis',
    label: 'Analysis',
    color: '#8b5cf6',
    features: [
      { label: 'Screener',          icon: SlidersHorizontal, path: '/screener',     color: '#06b6d4' },
      { label: 'Scanners',          icon: ScanLine,     path: '/scanners',          color: '#8b5cf6' },
      { label: 'Algo Flows',        icon: FlaskConical, path: '/algo-flows',        color: '#f59e0b', premium: true },
      { label: 'Backtest Lab',      icon: TestTube2,    path: '/backtest',          color: '#6366F1', premium: true },
      { label: 'Trade Performance', icon: PieChart,     path: '/trade-performance', color: '#06b6d4' },
      { label: 'Mkt Regime',        icon: Activity,     path: '/market-analysis',   color: '#0ea5e9', premium: true },
    ],
  },
  {
    id: 'trading',
    label: 'My Trading',
    color: '#10b981',
    features: [
      { label: 'Trade History',   icon: History,    path: '/trade-history',     color: '#06b6d4' },
      { label: 'Trade Analytics', icon: TrendingUp, path: '/trading-analytics', color: '#10b981', premium: true },
      { label: 'Calculator',      icon: Calculator, path: '/calculator',        color: '#f97316' },
      { label: 'App Guide',       icon: Map,        path: '/appguide',          color: '#f59e0b' },
      { label: 'Journey',         icon: Compass,    path: '/journey',           color: '#6c63ff' },
      { label: 'Capital',         icon: Wallet,     path: '/capital',           color: '#f59e0b' },
    ],
  },
  {
    id: 'learn',
    label: 'Learn',
    color: '#f59e0b',
    features: [
      { label: 'Learning', icon: BookOpen,   path: '/learning', color: '#10b981' },
    ],
  },
  {
    id: 'community',
    label: 'Community',
    color: '#f59e0b',
    features: [
      { label: 'Strategy Feed', icon: Users, path: '/forum', color: '#f59e0b' },
    ],
  },
];

const TOUR_ANCHORS = {
  '/act':               'discover-act',
  '/discipline':        'discover-discipline',
  '/risk-profile':      'discover-risk-profile',
  '/scanners':          'discover-scanners',
  '/algo-flows':        'discover-algo-flows',
  '/backtest':          'discover-backtest',
  '/market-analysis':   'discover-market-analysis',
  '/trading-analytics': 'discover-analytics',
  '/trade-history':     'discover-trade-history',
  '/calculator':        'discover-calculator',
  '/learning':          'discover-learning',
};

export default function DiscoverPage() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { meetsMinPlan } = usePermissions();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const handleTileClick = (path, premium) => {
    if (premium && !meetsMinPlan('premium')) {
      setUpgradeOpen(true);
      return;
    }
    navigate(path);
  };

  return (
    <div className="flex flex-col gap-5 pb-4">
      {CATEGORIES.map(({ id, label, color, features }) => (
        <div key={id}>
          <div className="flex items-center gap-2 mb-3">
            <span
              className="text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap"
              style={{ color }}
            >
              {label}
            </span>
            <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
          </div>

          <div className={id === 'how-to' ? '' : 'grid grid-cols-4 gap-2'}
            style={id === 'how-to' ? { display:'flex', flexDirection:'row', gap:8, overflowX:'auto', WebkitOverflowScrolling:'touch', scrollbarWidth:'none', msOverflowStyle:'none', paddingBottom:2 } : {}}>
            {features.map(({ label: flabel, icon: Icon, path, color: fcolor, premium }) => {
              const locked = premium && !meetsMinPlan('premium');
              return (
                <button
                  key={path}
                  onClick={() => handleTileClick(path, premium)}
                  data-tour={TOUR_ANCHORS[path]}
                  className="relative flex flex-col items-center gap-2 py-3 px-1 rounded-xl transition-all"
                  style={{
                    background: `linear-gradient(135deg, ${fcolor}15 0%, ${fcolor}05 100%)`,
                    border: `1px solid ${fcolor}25`,
                    opacity: locked ? 0.7 : 1,
                    ...(id === 'how-to' ? { flexShrink: 0, width: 80 } : {}),
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = `${fcolor}55`}
                  onMouseLeave={e => e.currentTarget.style.borderColor = `${fcolor}25`}
                >
                  {locked && (
                    <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-yellow-500/20 flex items-center justify-center">
                      <Lock className="w-2.5 h-2.5 text-yellow-400" />
                    </span>
                  )}
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${fcolor}18` }}
                  >
                    <Icon className="w-4 h-4" style={{ color: locked ? '#9ca3af' : fcolor }} />
                  </div>
                  <span
                    className="text-[10px] font-medium text-center leading-tight"
                    style={{ color: locked ? '#6b7280' : colors.primaryText }}
                  >
                    {flabel}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {upgradeOpen && (
        <UpgradeModal
          requiredPlan="premium"
          message="This feature is available on the Premium plan."
          onClose={() => setUpgradeOpen(false)}
        />
      )}
    </div>
  );
}
